import React, { FC, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BottomNavigation, BottomNavigationAction, Box, ButtonBase, CircularProgress, Paper, useTheme } from '@mui/material';
import { keyframes } from '@mui/system';
import HomeIcon from '@mui/icons-material/Home';
import ExploreIcon from '@mui/icons-material/Explore';
import InboxIcon from '@mui/icons-material/Inbox';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ControlPointIcon from '@mui/icons-material/ControlPoint';
import HideOnScroll from '../HideOnScroll';
import { useMobileComposeActionConfig, useMobileScrollHideEnabled } from './MobileHeaderContext';

interface MobileBottomNavProps {
  /** The app's scroll container, so the bar can hide on scroll-down. */
  scrollTarget?: HTMLElement | null;
}

const MobileBottomNav: FC<MobileBottomNavProps> = ({ scrollTarget }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  // Only feed/post pages opt into hide-on-scroll.
  const scrollHide = useMobileScrollHideEnabled();
  // When a page hands over a primary action (composing a post), the bar stops
  // being navigation and becomes that one button.
  const composeAction = useMobileComposeActionConfig();
  const composing = Boolean(composeAction);

  /*
   * The action is cleared the instant the compose page unmounts, but the
   * button takes 280ms to shrink back into the FAB. Rendering straight from
   * `composeAction` made the label vanish on frame one, so the pill collapsed
   * empty — which read as a snap rather than an animation. Holding the last
   * label lets the text ride the transition out.
   */
  const [lastLabel, setLastLabel] = useState('');
  useEffect(() => {
    if (composeAction) {
      setLastLabel(composeAction.busy ? composeAction.busyLabel : composeAction.label);
    }
  }, [composeAction]);

  const pulseAnimation = keyframes`
    0% {
      transform: ${theme.tokens.animations.pulseStart};
      opacity: 0;
    }
    50% {
      opacity: 1;
    }
    100% {
      transform: ${theme.tokens.animations.pulseEnd};
      opacity: 1;
    }
  `;

  const getCurrentRoute = () => {
    const path = location.pathname;
    if (path === '/') return 0;
    if (path.startsWith('/herd/') || path.includes('/explore')) return 1;
    if (path.startsWith('/submit')) return 2;
    if (path.includes('/messages')) return 3;
    if (path.startsWith('/shop')) return 4;
    // -1 rather than 0: an unrecognised route must not light up Home.
    return -1;
  };

  const handleNavigate = (_event: React.SyntheticEvent, newValue: number) => {
    switch (newValue) {
      case 0:
        navigate('/');
        break;
      case 1:
        navigate('/explore');
        break;
      case 2:
        navigate('/submit');
        break;
      case 3:
        navigate('/messages');
        break;
      case 4:
        navigate('/shop');
        break;
      default:
        break;
    }
  };

  const currentRoute = getCurrentRoute();

  // Guide .mnav .it: mono label under the icon, active icon sits in an
  // accent-dim pill indicator instead of a dot.
  const actionSx = {
    color: theme.tokens.colors.text3,
    minWidth: 56,
    transition: `color ${theme.tokens.animations.quick} ${theme.tokens.animations.easing}`,
    '& .ug-ind': {
      padding: '4px 16px',
      borderRadius: 'var(--sh-full)',
      display: 'flex',
      transition: `background-color var(--dur-m3) var(--ease-m3)`,
    },
    '&.Mui-selected': {
      color: theme.tokens.colors.textPrimary,
      '& .ug-ind': {
        color: theme.palette.primary.main,
        animation: `${pulseAnimation} ${theme.tokens.animations.deliberate} ${theme.tokens.animations.easingOut}`,
      },
    },
  };

  // Guide .mnav .fab: the create action is a raised 16px-radius accent tile.
  const createActionSx = {
    flex: '0 0 auto',
    mt: '-32px',
    mx: 1,
    width: 50,
    height: 50,
    minWidth: 50,
    p: 0,
    borderRadius: 'var(--sh-lg)',
    backgroundColor: theme.palette.primary.main,
    color: theme.tokens.colors.onPrimary,
    boxShadow: '0 10px 28px rgba(229,9,20,0.4)',
    '&.Mui-selected': {
      color: theme.tokens.colors.onPrimary,
    },
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
  };

  const bar = (
    <Paper
      sx={{
        position: 'fixed',
        // Fixed elements anchor to the layout viewport, which the keyboard does
        // not shrink — without this the Post button hides behind the keyboard.
        bottom: 'var(--keyboard-inset-height, 0px)',
        left: 0,
        right: 0,
        zIndex: theme.tokens.zIndex.overlay,
        borderTop: 'var(--border-thin-solid)',
        borderColor: theme.tokens.colors.glassBorder,
        pb: 'env(safe-area-inset-bottom)',
        // Frosted glass — content scrolls behind and shows through blurred.
        backgroundColor: theme.tokens.colors.glassBackground,
        backdropFilter: theme.tokens.effects.glassBlur,
        WebkitBackdropFilter: theme.tokens.effects.glassBlur,
      }}
      elevation={0}
    >
      <Box sx={{ position: 'relative' }}>
      <BottomNavigation
        value={currentRoute}
        onChange={handleNavigate}
        showLabels
        aria-hidden={composing}
        sx={{
          backgroundColor: 'transparent',
          alignItems: 'center',
          overflow: 'visible',
          // Fades out under the expanding action rather than unmounting, so the
          // bar keeps its height and nothing reflows mid-animation.
          opacity: composing ? 0 : 1,
          pointerEvents: composing ? 'none' : 'auto',
          transition: `opacity ${theme.tokens.animations.fast} ${theme.tokens.animations.easeM3}`,
        }}
      >
        <BottomNavigationAction
          label="Home"
          icon={
            <Box component="span" className="ug-ind">
              <HomeIcon />
            </Box>
          }
          sx={actionSx}
        />
        <BottomNavigationAction
          label="Explore"
          icon={
            <Box component="span" className="ug-ind">
              <ExploreIcon />
            </Box>
          }
          sx={actionSx}
        />
        <BottomNavigationAction
          icon={<ControlPointIcon />}
          sx={createActionSx}
        />
        <BottomNavigationAction
          label="Inbox"
          icon={
            <Box component="span" className="ug-ind">
              <InboxIcon />
            </Box>
          }
          sx={actionSx}
        />
        <BottomNavigationAction
          label="Store"
          icon={
            <Box component="span" className="ug-ind">
              <StorefrontIcon />
            </Box>
          }
          sx={actionSx}
        />
      </BottomNavigation>

      {/*
        The create action, expanded. It starts life at exactly the FAB's
        footprint and grows to fill the bar, so the two cross-fade in place and
        it reads as the button itself stretching out — no second control appears.
      */}
      <ButtonBase
        onClick={() => composeAction?.onSubmit()}
        disabled={!composing || composeAction?.disabled || composeAction?.busy}
        aria-hidden={!composing}
        tabIndex={composing ? 0 : -1}
        sx={{
          position: 'absolute',
          height: 50,
          // Collapsed geometry matches the FAB: 50px wide, centred, raised 32px.
          top: composing ? 3 : -32,
          left: composing ? 12 : 'calc(50% - 25px)',
          right: composing ? 12 : 'calc(50% - 25px)',
          borderRadius: 'var(--sh-lg)',
          backgroundColor: theme.palette.primary.main,
          color: theme.tokens.colors.onPrimary,
          boxShadow: '0 10px 28px rgba(229,9,20,0.4)',
          fontFamily: theme.tokens.typography.sansFontFamily,
          fontWeight: theme.tokens.typography.fontWeightBold,
          fontSize: '15px',
          gap: 1,
          opacity: composing ? 1 : 0,
          pointerEvents: composing ? 'auto' : 'none',
          transition: `top var(--dur-m3) var(--ease-m3), left var(--dur-m3) var(--ease-m3), right var(--dur-m3) var(--ease-m3), opacity ${theme.tokens.animations.fast} ${theme.tokens.animations.easeM3}`,
          '&.Mui-disabled': {
            opacity: composing ? 0.55 : 0,
            color: theme.tokens.colors.onPrimary,
          },
        }}
      >
        {composeAction?.busy && <CircularProgress size={16} thickness={6} color="inherit" />}
        {lastLabel}
      </ButtonBase>
      </Box>
    </Paper>
  );

  return scrollHide ? (
    <HideOnScroll target={scrollTarget} direction="up">
      {bar}
    </HideOnScroll>
  ) : (
    bar
  );
};

export default MobileBottomNav;
